/** @jsx jsx */
import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import { Button, FooterBtnStyle, SpanButton } from '@styles/button.style'
import { ModalBg } from '@styles/base.style'
import { Modal } from 'antd-mobile'
import { jsx } from '@emotion/core'
import { Input, Popover } from 'antd'
import { AttachedIco } from '@shared/Icon/MessageIcon'
import { SpotsQuickMenuList } from '@styles/modal'
import { FavoriteUserAlertMessageWrap } from '@views/Popup/FavoriteUserAlertMessage/index.style'
import useRootStore from '@store/useRootStore'
import * as Utils from '@utils/utils'
import { TOAST_TYPE } from '@utils/utils'
import { MESSAGE_IMAGE_BASE_URL } from '@constants/index'
import _ from 'lodash'
import DialogUtils from '@utils/dialog-utils'
import classNames from 'classnames'

interface FavoriteUserAlertMessageProps {
  visible: boolean
  onClose: () => void
}

const FavoriteUserAlertMessage: React.FC<FavoriteUserAlertMessageProps> = ({ visible, onClose }) => {
  const { TextArea } = Input
  const { userStore, globalStore } = useRootStore()
  const [enabled, setEnabled] = useState(false)
  const [content, setContent] = useState('')
  const [attachedUrl, setAttachedUrl] = useState('')
  const [attachedImage, setAttachedImage] = useState('')
  const [attachedImageFile, setAttachedImageFile] = useState<File | null>(null)
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)
  const [visiblePopover, setVisiblePopover] = useState(false)
  const [visibleSharedLink, setVisibleSharedLink] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const fileInputComponent = useRef<HTMLInputElement>(null)
  const linkInputValueRef = useRef('')

  useEffect(() => {
    if (!visible) {
      return
    }

    let mounted = true
    userStore.getFavoriteAlarmSetting().then(setting => {
      if (!mounted || !setting) {
        return
      }
      setEnabled(Boolean(setting.enabled))
      setContent(setting.content || '')
      setAttachedUrl(setting.attachedUrl || '')
      setAttachedImage(setting.attachedImage || '')
      setAttachedImageFile(null)
      setPreviewImageUrl(null)
      setVisibleSharedLink(!_.isEmpty(setting.attachedUrl))
      if (fileInputComponent.current) {
        fileInputComponent.current.value = ''
      }
    })

    return () => {
      mounted = false
    }
  }, [visible, userStore])

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0]
    if (!file) {
      return
    }
    if (!Utils.maxImageFileSizeValidate(file)) {
      event.target.value = ''
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      setPreviewImageUrl(reader.result as string)
      setAttachedImageFile(file)
      setAttachedImage('')
    }
    reader.readAsDataURL(file)
    setVisiblePopover(false)
  }

  const onRemoveImage = () => {
    setAttachedImageFile(null)
    setPreviewImageUrl(null)
    setAttachedImage('')
    if (fileInputComponent.current) {
      fileInputComponent.current.value = ''
    }
  }

  const onClickAttachUrlButton = () => {
    setVisiblePopover(false)
    linkInputValueRef.current = attachedUrl
    DialogUtils.showMessageLinkAttachDialog({
      value: linkInputValueRef.current,
      onChange: event => {
        linkInputValueRef.current = _.trim(event.target.value)
      },
      callbackCloseDialog: () => {
        const url = linkInputValueRef.current
        setAttachedUrl(url)
        setVisibleSharedLink(!_.isEmpty(url))
      },
    })
  }

  const onClickMoveToUrl = () => {
    if (_.isEmpty(attachedUrl) || !Utils.checkUrl(attachedUrl)) {
      Utils.showToast(TOAST_TYPE.WARN, '올바른 URL 링크를 입력해 주세요.')
      return
    }
    globalStore.toMoveWebHomePage(attachedUrl, 'popup')
  }

  const onSave = async () => {
    const trimmedContent = _.trim(content)
    const trimmedUrl = _.trim(attachedUrl)
    if (enabled && _.isEmpty(trimmedContent)) {
      Utils.showToast(TOAST_TYPE.WARN, '내용을 입력해주세요.')
      return
    }
    if (!_.isEmpty(trimmedUrl) && !Utils.checkUrl(trimmedUrl)) {
      Utils.showToast(TOAST_TYPE.WARN, '올바른 URL 링크를 입력해 주세요.')
      return
    }

    const formData = new FormData()
    formData.append('enabled', String(enabled))
    formData.append('content', trimmedContent)
    formData.append('attachedUrl', trimmedUrl)
    if (!attachedImageFile && !_.isEmpty(attachedImage)) {
      formData.append('attachedImage', attachedImage)
    }
    if (attachedImageFile) {
      formData.append('image', attachedImageFile)
    }

    setIsSaving(true)
    const saved = await userStore.saveFavoriteAlarmSetting(formData)
    setIsSaving(false)
    if (saved) {
      onClose()
    }
  }

  const contentsBoxClass = classNames('message-contents-box', { 'disabled': !enabled })

  const attachedList = (
    <SpotsQuickMenuList className="message-quick-list">
      <ul className="quick-menu-list">
        <li className="quick-menu-list--item">
          <Button className="quick-btn">
            이미지
            <input type="file"
                   ref={fileInputComponent}
                   accept="image/jpg,image/jpeg,image/png,image/gif,image/bmp,image/tif,image/tiff"
                   name="profile_img"
                   className="quick-btn--input"
                   disabled={!enabled}
                   onChange={onFileChange}/>
          </Button>
        </li>
        <li className="quick-menu-list--item">
          <Button
            className="quick-btn"
            onClick={onClickAttachUrlButton}
          >
            링크
          </Button>
        </li>
      </ul>
    </SpotsQuickMenuList>
  )

  return (
    <Modal popup visible={visible} maskClosable={false} animated={isMobile} animationType="slide-up" onClose={onClose}
           style={{ height: 'auto' }} className="short-top pc-close-btn-modal white-popup message-popup favorite-user-alert-message-popup">
      <FavoriteUserAlertMessageWrap>
        <div className="header">
          <h3 className="header--title">인사말 설정</h3>
          <input type="checkbox" id="favorite-user-alert" name="sendFavoriteUserAlert" checked={enabled}
                 onChange={event => setEnabled(event.target.checked)} className="header--switch"/>
          <label htmlFor="favorite-user-alert" aria-label="인사말 자동 발송 설정" className="on-off-wrap"/>
        </div>
        <div id="messageContentsBox" className={contentsBoxClass}>
          <div className="message-input-wrap">
            <TextArea maxLength={600} placeholder="내용을 입력해주세요.(600자 이내)" className="contents text-input"
                      value={content} onChange={event => setContent(event.target.value)} disabled={!enabled}/>
            <Popover visible={visiblePopover} onVisibleChange={setVisiblePopover} placement="bottomRight" content={attachedList}
                     trigger="click" overlayClassName="betting-history-quick-menu"
                     getPopupContainer={() => document.getElementById('messageContentsBox')}>
              <Button className="attached-btn" disabled={!enabled}><AttachedIco/></Button>
            </Popover>
          </div>
          {(previewImageUrl || attachedImage) && (
            <div className="image-preview-wrap">
              <div className="upload-img-preview">
                <img src={previewImageUrl || `${MESSAGE_IMAGE_BASE_URL}${attachedImage}`}
                     alt="이미지 미리보기"/>
              </div>
              <Button className="delete-img-btn" onClick={onRemoveImage} disabled={!enabled}><span className="x-ico"/></Button>
            </div>
          )}
          {visibleSharedLink && (
            <div className="shared link url">
              <div className="link-wrap">
                <SpanButton onClick={onClickMoveToUrl} className="attached-link">{attachedUrl}</SpanButton>
              </div>
            </div>
          )}
        </div>
        <div className="footer">
          <Button css={FooterBtnStyle} onClick={onSave} disabled={isSaving} className="one-btn dark">저장</Button>
        </div>
      </FavoriteUserAlertMessageWrap>
      <ModalBg onClick={onClose}/>
    </Modal>
  )
}

export default observer(FavoriteUserAlertMessage)
